import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { isPast, isFuture } from 'date-fns'

import Chip from '../../../../../../../components/Chip'

import { getFormattedAdStatusProps } from '../helper'

import {
  lineItemSelector,
  lineItemsSelector,
  lineItemsWasLoadedSelector,
  lineItemWasLoadedSelector
} from '../../../../../../../modules/selectors/lineItems'

import { CHIP_COLORS } from '../../../../../../../constants/other'

const EffectiveStatusChip = ({ ad }) => {
  let effectiveStatus = ad?.effective_status

  const lineItemWasLoaded = useSelector(lineItemWasLoadedSelector)
  const lineItemsWasLoaded = useSelector(lineItemsWasLoadedSelector)
  const lineItem = useSelector(lineItemSelector)
  const lineItems = useSelector(lineItemsSelector)

  const parentLineItem = useMemo(() => {
    if (lineItemWasLoaded) {
      return lineItem
    } else if (lineItemsWasLoaded) {
      return lineItems.find(item => item.id === ad.line_item_id)
    }
    return lineItem
  }, [lineItemWasLoaded, lineItem, lineItemsWasLoaded, lineItems, ad.line_item_id])

  const chipProps = useMemo(() => {
    if (effectiveStatus === 'active') {
      const parentLineItemDateStart = parentLineItem['time_start'] || parentLineItem['date_start']
      const parentLineItemDateStop = parentLineItem['time_stop'] || parentLineItem['date_stop']

      const isParentLineItemStartDateInThePast = isPast(parentLineItemDateStart)
      const isParentLineItemStartDateInTheFuture = isFuture(parentLineItemDateStart)
      const isParentLineItemEndDateInThePast = isPast(parentLineItemDateStop)
      const isParentLineItemEndDateInTheFuture = isFuture(parentLineItemDateStop)

      if (isParentLineItemEndDateInThePast) {
        return {
          text: 'Completed',
          color: CHIP_COLORS.success
        }
      }

      if (isParentLineItemStartDateInTheFuture) {
        return {
          text: 'Ready',
          color: CHIP_COLORS.success
        }
      }

      if (isParentLineItemStartDateInThePast && isParentLineItemEndDateInTheFuture) {
        return {
          text: 'Active',
          color: CHIP_COLORS.success
        }
      }

      return {}
    } else if (effectiveStatus === 'paused') {
      return {
        text: 'off',
        color: CHIP_COLORS.neutral
      }
    } else if (effectiveStatus === 'deleted') {
      return {
        text: 'Deleted',
        color: CHIP_COLORS.neutral
      }
    } else if (effectiveStatus === 'pending_review') {
      return {
        text: 'In Review',
        color: CHIP_COLORS.success
      }
    } else if (effectiveStatus === 'preapprioved') {
      return {
        text: 'Preparing',
        color: CHIP_COLORS.success
      }
    } else if (effectiveStatus === 'pending_billing_info') {
      return {
        text: 'Requires Admin Action',
        color: CHIP_COLORS.error
      }
    } else if (effectiveStatus === 'campaign_paused') {
      return {
        text: 'Campaign Paused',
        color: CHIP_COLORS.neutral
      }
    } else if (effectiveStatus === 'archived') {
      return {
        text: 'Archived',
        color: CHIP_COLORS.neutral
      }
    } else if (effectiveStatus === 'adset_paused') {
      return {
        text: 'Line Item Paused',
        color: CHIP_COLORS.neutral
      }
    } else if (effectiveStatus === 'in_process') {
      return {
        text: 'Processing',
        color: CHIP_COLORS.success
      }
    } else if (effectiveStatus === 'with_issues') {
      return {
        text: 'Issues Detected',
        color: CHIP_COLORS.error
      }
    } else {
      return {
        text: '',
        color: ''
      }
    }
  }, [effectiveStatus, parentLineItem])

  const { status: mainStatusText } = getFormattedAdStatusProps(ad)

  const showEffectiveStatusChip =
    parentLineItem &&
    effectiveStatus &&
    chipProps.text &&
    // don't show second status if they both are equal
    chipProps.text.toLowerCase() !== mainStatusText.toLowerCase()

  if (!showEffectiveStatusChip) {
    return null
  }

  return <Chip text={chipProps.text} color={chipProps.color} isBackgroundTransparent={true} />
}

export default EffectiveStatusChip
